import { useEffect, useState } from 'react';
import { makeStyles, Typography } from '@material-ui/core';

export interface StreamingIndicatorProps {
  isStreaming: boolean;
  isThinking?: boolean;
  currentTool?: string;
  toolCount: number;
}

const useStyles = makeStyles(theme => ({
  root: {
    alignSelf: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: theme.spacing(1, 2),
    borderRadius: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[100],
    borderLeft: `3px solid ${theme.palette.secondary.main}`,
    borderBottomLeftRadius: theme.spacing(0.5),
    minWidth: 160,
  },
  thinkingRoot: {
    alignSelf: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.spacing(1.5),
    borderBottomLeftRadius: theme.spacing(0.5),
    minWidth: 180,
    backgroundColor: 'rgba(250, 70, 22, 0.04)',
    borderLeft: `3px solid ${theme.palette.secondary.main}`,
    animation: '$shimmer 2s ease-in-out infinite',
  },
  '@keyframes shimmer': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.7 },
  },
  thinkingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  thinkingIcon: {
    fontSize: '0.85rem',
    animation: '$pulse 1.5s ease-in-out infinite',
  },
  '@keyframes pulse': {
    '0%, 100%': { transform: 'scale(1)' },
    '50%': { transform: 'scale(1.15)' },
  },
  thinkingText: {
    fontSize: '0.78rem',
    color: theme.palette.secondary.main,
    fontWeight: 600,
    letterSpacing: '0.02em',
  },
  thinkingSubtext: {
    fontSize: '0.65rem',
    color: theme.palette.text.hint,
    fontStyle: 'italic',
  },
  topRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  statusText: {
    fontSize: '0.72rem',
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  dots: {
    display: 'inline-flex',
    gap: 3,
    marginLeft: theme.spacing(0.5),
    '& span': {
      width: 6,
      height: 6,
      borderRadius: '50%',
      backgroundColor: theme.palette.secondary.main,
      animation: '$bounce 1.4s infinite ease-in-out both',
      '&:nth-child(1)': { animationDelay: '0s' },
      '&:nth-child(2)': { animationDelay: '0.2s' },
      '&:nth-child(3)': { animationDelay: '0.4s' },
    },
  },
  '@keyframes bounce': {
    '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
    '40%': { opacity: 1, transform: 'scale(1)' },
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap' as const,
  },
  metaChip: {
    fontSize: '0.62rem',
    color: theme.palette.text.hint,
    backgroundColor: theme.palette.grey[200],
    borderRadius: 8,
    padding: '1px 6px',
    whiteSpace: 'nowrap' as const,
  },
  toolChip: {
    fontSize: '0.62rem',
    color: theme.palette.secondary.main,
    backgroundColor: 'rgba(250, 70, 22, 0.08)',
    borderRadius: 8,
    padding: '1px 6px',
    whiteSpace: 'nowrap' as const,
    maxWidth: 200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

function formatElapsed(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m${s.toString().padStart(2, '0')}s`;
}

export const StreamingIndicator = ({
  isStreaming,
  isThinking,
  currentTool,
  toolCount,
}: StreamingIndicatorProps) => {
  const classes = useStyles();
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!isStreaming) {
      setElapsed(0);
      return undefined;
    }
    setElapsed(0);
    const interval = setInterval(() => setElapsed(prev => prev + 1), 1000);
    return () => clearInterval(interval);
  }, [isStreaming]);

  if (!isStreaming) return null;

  if (isThinking) {
    return (
      <div className={classes.thinkingRoot}>
        <div className={classes.thinkingRow}>
          <span
            className={classes.thinkingIcon}
            role="img"
            aria-label="Pensando"
          >
            &#x1F9E0;
          </span>
          <Typography className={classes.thinkingText}>Pensando...</Typography>
        </div>
        <Typography className={classes.thinkingSubtext}>
          Analisando contexto e planejando resposta
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.topRow}>
        <Typography className={classes.statusText}>
          Agente processando
        </Typography>
        <span className={classes.dots} aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
      </div>
      <div className={classes.metaRow}>
        <span className={classes.metaChip}>{formatElapsed(elapsed)}</span>
        {toolCount > 0 && (
          <span className={classes.metaChip}>
            {toolCount} ferramenta{toolCount > 1 ? 's' : ''}
          </span>
        )}
        {currentTool && <span className={classes.toolChip}>{currentTool}</span>}
      </div>
    </div>
  );
};
